import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { FieldOverride } from '@app/interfaces/dynamicFields';
import { IHuntersFilterOptions } from '@app/interfaces/hunter';
import { tableDataInit } from '@app/pages/HuntingOperations/Hunters/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface HuntersFilterState {
  overrides: FieldOverride[];
  filterOptions: IHuntersFilterOptions;
  shouldTriggerFetch: boolean;
  multiSearch: string;
  selectedStatuses: string[];
  selectedStatusesTemp: string[];
  selectedMembershipStatusIds: FilterIdsType;
  selectedMembershipStatusIdsTemp: FilterIdsType;
  selectedMembershipTypeIds: FilterIdsType;
  selectedMembershipTypeIdsTemp: FilterIdsType;
  selectedPaymentDoneIds: FilterIdsType;
  selectedPaymentDoneIdsTemp: FilterIdsType;
  includeSoftDeleted: boolean;
}

const initialState: HuntersFilterState = {
  overrides: [],
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: true,
  multiSearch: '',
  selectedStatuses: [],
  selectedStatusesTemp: [],
  selectedMembershipStatusIds: [],
  selectedMembershipStatusIdsTemp: [],
  selectedMembershipTypeIds: [],
  selectedMembershipTypeIdsTemp: [],
  selectedPaymentDoneIds: [],
  selectedPaymentDoneIdsTemp: [],
  includeSoftDeleted: false,
};

const huntersFiltersSlice = createSlice({
  name: 'huntersFilters',
  initialState,
  reducers: {
    setOverrides(state, action: PayloadAction<FieldOverride[]>) {
      state.overrides = action.payload;
    },
    setFilterOptions(state, action: PayloadAction<IHuntersFilterOptions>) {
      state.filterOptions = action.payload;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setMultiSearch(state, action: PayloadAction<string>) {
      state.multiSearch = action.payload;
    },
    setSelectedStatuses(state, action: PayloadAction<string[]>) {
      state.selectedStatuses = action.payload;
    },
    setSelectedStatusesTemp(state, action: PayloadAction<string[]>) {
      state.selectedStatusesTemp = action.payload;
    },
    setSelectedMembershipStatusIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipStatusIds = action.payload;
    },
    setSelectedMembershipStatusIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipStatusIdsTemp = action.payload;
    },
    setSelectedMembershipTypeIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipTypeIds = action.payload;
    },
    setSelectedMembershipTypeIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipTypeIdsTemp = action.payload;
    },
    setSelectedPaymentDoneIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedPaymentDoneIds = action.payload;
    },
    setSelectedPaymentDoneIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedPaymentDoneIdsTemp = action.payload;
    },
    setIncludeSoftDeleted(state, action: PayloadAction<boolean>) {
      state.includeSoftDeleted = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.shouldTriggerFetch = true;
      state.multiSearch = initialState.multiSearch;
      state.selectedStatuses = initialState.selectedStatuses;
      state.selectedStatusesTemp = initialState.selectedStatusesTemp;
      state.selectedMembershipStatusIds = initialState.selectedMembershipStatusIds;
      state.selectedMembershipStatusIdsTemp = initialState.selectedMembershipStatusIdsTemp;
      state.selectedMembershipTypeIds = initialState.selectedMembershipTypeIds;
      state.selectedMembershipTypeIdsTemp = initialState.selectedMembershipTypeIdsTemp;
      state.selectedPaymentDoneIds = initialState.selectedPaymentDoneIds;
      state.selectedPaymentDoneIdsTemp = initialState.selectedPaymentDoneIdsTemp;
      state.includeSoftDeleted = initialState.includeSoftDeleted;
    },
  },
});

export const {
  setOverrides,
  setFilterOptions,
  setShouldTriggerFetch,
  setMultiSearch,
  setSelectedStatuses,
  setSelectedStatusesTemp,
  setSelectedMembershipStatusIds,
  setSelectedMembershipStatusIdsTemp,
  setSelectedMembershipTypeIds,
  setSelectedMembershipTypeIdsTemp,
  setSelectedPaymentDoneIds,
  setSelectedPaymentDoneIdsTemp,
  setIncludeSoftDeleted,
  resetFilters,
} = huntersFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { huntersFilters: HuntersFilterState }) => {
  const {
    multiSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  } = state.huntersFilters;

  return (
    !!multiSearch ||
    !!selectedStatuses.length ||
    !!selectedMembershipStatusIds.length ||
    !!selectedMembershipTypeIds.length ||
    !!selectedPaymentDoneIds.length ||
    includeSoftDeleted
  );
};

export const activeFiltersCountSelector = (state: { huntersFilters: HuntersFilterState }) => {
  const {
    multiSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  } = state.huntersFilters;

  let count = 0;
  if (multiSearch) count++;
  if (selectedStatuses.length > 0) count++;
  if (selectedMembershipStatusIds.length > 0) count++;
  if (selectedMembershipTypeIds.length > 0) count++;
  if (selectedPaymentDoneIds.length > 0) count++;
  if (includeSoftDeleted) count++;

  return count;
};

export const fetchParamsSelector = (state: { huntersFilters: HuntersFilterState }) => {
  const {
    multiSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  } = state.huntersFilters;

  return {
    multiSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  };
};

export const huntersFiltersActions = huntersFiltersSlice.actions;

export const huntersFilters = huntersFiltersSlice.reducer;
